import React from 'react';
import Icon from '../../../components/AppIcon';

const EventFeed = ({ events }) => {
  const getColorClasses = (color) => {
    const colorMap = {
      success: 'bg-success/10 text-success border-success/20',
      error: 'bg-error/10 text-error border-error/20',
      warning: 'bg-warning/10 text-warning border-warning/20',
      primary: 'bg-primary/10 text-primary border-primary/20',
      accent: 'bg-accent/10 text-accent border-accent/20'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const eventTime = new Date(timestamp);
    const diffMs = now - eventTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-error/10 text-error">
          High Priority
        </span>
      );
    }
    return null;
  };

  if (!events || events?.length === 0) {
    return (
      <div className="card">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Icon name="Activity" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Events Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            No events match your current filters. Try adjusting your search criteria or wait for new activity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">Event Stream</h2>
        <span className="text-sm text-muted-foreground">
          {events?.length} {events?.length === 1 ? 'event' : 'events'}
        </span>
      </div>

      <div className="space-y-3">
        {events?.map((event) => (
          <div 
            key={event?.id}
            className="card hover:shadow-lg transition-all duration-250 border-l-4"
            style={{ borderLeftColor: `var(--color-${event?.color})` }}
          >
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getColorClasses(event?.color)}`}>
                <Icon name={event?.icon} size={24} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-foreground">{event?.title}</h3>
                      {getPriorityBadge(event?.priority)}
                    </div>
                    <p className="text-sm text-muted-foreground">{event?.description}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {getTimeAgo(event?.timestamp)}
                  </span>
                </div>

                {/* Event Details */}
                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mt-3">
                  <span className={`px-2 py-1 rounded-full ${getColorClasses(event?.color)}`}>
                    {event?.category}
                  </span>
                  
                  {event?.memberName && (
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      {event?.memberName}
                    </span>
                  )}
                  
                  {event?.location && (
                    <span className="flex items-center gap-1">
                      <Icon name="MapPin" size={12} />
                      {event?.location}
                    </span>
                  )}
                  
                  {event?.amount && (
                    <span className="flex items-center gap-1 font-medium">
                      <Icon name="DollarSign" size={12} />
                      {event?.amount}
                    </span>
                  )}
                  
                  {event?.className && (
                    <span className="flex items-center gap-1">
                      <Icon name="Dumbbell" size={12} />
                      {event?.className}
                    </span>
                  )}
                  
                  {event?.equipment && (
                    <span className="flex items-center gap-1">
                      <Icon name="Package" size={12} />
                      {event?.equipment}
                    </span>
                  )}
                </div>

                {/* Quick Actions */}
                {event?.priority === 'high' && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <button className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors focus-ring">
                      Take Action
                    </button>
                    <button className="px-3 py-1.5 text-xs font-medium rounded-lg bg-muted text-foreground hover:bg-muted/80 transition-colors focus-ring">
                      View Details
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventFeed;